import React, { useEffect, useState } from 'react';

import { useIntake } from '../context/IntakeContext';
import { useStepNav } from '../hooks/useStepNav';
import { getInsurers } from '../services/intakeApi';
import StepLayout from '../components/StepLayout';
import Field from '../components/Field';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { cn } from '../lib/utils';

/**
 * Step 3 — what happened, and who is paying.
 *
 * The insurance branch is shown inline rather than as its own step: it is two
 * or three fields, and a step that appears for some customers and not others
 * makes the progress indicator lie. The loan vehicle and optional extras live
 * on the next step so neither page becomes a wall of checkboxes.
 */
const Choice = ({ selected, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={cn(
      'flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors',
      selected
        ? 'bg-[var(--brand-accent)] text-[var(--brand-accent-foreground)]'
        : 'bg-input text-foreground hover:bg-accent'
    )}
  >
    {children}
  </button>
);

const DamageStep = () => {
  const { form, update, save } = useIntake();
  const go = useStepNav();

  const [insurers, setInsurers] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    getInsurers()
      .then((data) => { if (!cancelled) setInsurers(data || []); })
      .catch(() => { if (!cancelled) setInsurers([]); });
    return () => { cancelled = true; };
  }, []);

  const handleNext = () => {
    const next = {};
    if (!form.damageDescription?.trim()) {
      next.damageDescription = 'Please tell the repairer what happened';
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    save();
    go('workshop');
  };

  return (
    <StepLayout
      title="The damage"
      onBack={() => go('vehicle')}
      onNext={handleNext}
    >
      <Field label="What happened?" required error={errors.damageDescription}>
        {({ id }) => (
          <Textarea
            id={id}
            name="damageDescription"
            rows={4}
            placeholder="Reversed into a pole in a car park — dent and scratches on the rear left corner."
            value={form.damageDescription}
            onChange={(event) => update({ damageDescription: event.target.value })}
          />
        )}
      </Field>

      <Field label="Is this an insurance claim?">
        <div className="flex gap-2">
          <Choice
            selected={form.isInsuranceWork === true}
            onClick={() => update({ isInsuranceWork: true })}
          >
            Yes, through insurance
          </Choice>
          <Choice
            selected={form.isInsuranceWork === false}
            onClick={() => update({
              isInsuranceWork: false,
              insuranceCompany: '',
              otherInsurer: '',
              claimMade: '',
              excess: ''
            })}
          >
            No, paying privately
          </Choice>
        </div>
      </Field>

      {form.isInsuranceWork && (
        <div className="flex flex-col gap-5 rounded-lg bg-muted/50 p-4">
          <Field label="Insurance company">
            {({ id }) => (
              <select
                id={id}
                name="insuranceCompany"
                className="flex h-10 w-full rounded-md bg-input px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:text-sm"
                value={form.insuranceCompany || ''}
                onChange={(event) => update({
                  insuranceCompany: event.target.value,
                  otherInsurer: event.target.value === 'other' ? form.otherInsurer : ''
                })}
              >
                <option value="">Select your insurer</option>
                {insurers.map((insurer) => (
                  <option key={insurer.id} value={insurer.id}>{insurer.name}</option>
                ))}
                <option value="other">Other / not listed</option>
              </select>
            )}
          </Field>

          {form.insuranceCompany === 'other' && (
            <Field label="Insurer name">
              {({ id }) => (
                <Input
                  id={id}
                  name="otherInsurer"
                  value={form.otherInsurer}
                  onChange={(event) => update({ otherInsurer: event.target.value })}
                />
              )}
            </Field>
          )}

          <Field label="Have you lodged a claim?">
            <div className="flex gap-2">
              <Choice selected={form.claimMade === 'yes'} onClick={() => update({ claimMade: 'yes' })}>
                Yes
              </Choice>
              <Choice selected={form.claimMade === 'not-yet'} onClick={() => update({ claimMade: 'not-yet' })}>
                Not yet
              </Choice>
            </div>
          </Field>

          <Field label="Excess">
            {({ id }) => (
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id={id}
                  name="excess"
                  inputMode="decimal"
                  className="pl-7"
                  placeholder="0.00"
                  value={form.excess}
                  onChange={(event) => {
                    const [whole, ...rest] = event.target.value.replace(/[^0-9.]/g, '').split('.');
                    update({ excess: rest.length ? `${whole}.${rest.join('').slice(0, 2)}` : whole });
                  }}
                />
              </div>
            )}
          </Field>
        </div>
      )}

    </StepLayout>
  );
};

export default DamageStep;
